import React from 'react';
import { ShieldCheck } from 'lucide-react';

// ---------------------------------------------------------------------------
// Dot sizes & color palettes
// ---------------------------------------------------------------------------
const DOT_SIZES = {
  sm: 'w-1.5 h-1.5',
  md: 'w-2.5 h-2.5',
  lg: 'w-3 h-3',
};

const PALETTES = {
  blue: { dot: 'bg-blue-500', glow: 'rgba(59,130,246,0.35)', ring: 'border-blue-200', from: 'from-blue-600', to: 'to-cyan-500', text: 'text-gray-600' },
  emerald: { dot: 'bg-emerald-500', glow: 'rgba(16,185,129,0.35)', ring: 'border-emerald-200', from: 'from-emerald-600', to: 'to-teal-500', text: 'text-gray-600' },
  indigo: { dot: 'bg-indigo-500', glow: 'rgba(99,102,241,0.35)', ring: 'border-indigo-200', from: 'from-indigo-600', to: 'to-blue-500', text: 'text-gray-600' },
};

// ---------------------------------------------------------------------------
// Bouncing Dots — the unifying design element
// ---------------------------------------------------------------------------
const BouncingDots = ({ size = 'md', color = 'blue' }) => {
  const dotClass = DOT_SIZES[size] || DOT_SIZES.md;
  const p = PALETTES[color] || PALETTES.blue;

  return (
    <div className="flex items-center justify-center gap-1.5">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className={`${dotClass} ${p.dot} rounded-full animate-bounce-dot`}
          style={{
            animationDelay: `${i * 0.16}s`,
            boxShadow: `0 0 6px 2px ${p.glow}`,
          }}
        />
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tier 1 — Full-page "Digital Pulse" (sign-in, route protection)
// ---------------------------------------------------------------------------
const PageLoader = ({ message, color = 'blue' }) => {
  const p = PALETTES[color] || PALETTES.blue;

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100">
      <div className="flex-grow flex flex-col items-center justify-center">
        {/* Shield with pulse rings */}
        <div className="relative mb-8">
          <div
            className={`absolute inset-0 rounded-2xl border-2 ${p.ring} animate-pulse-ring`}
          />
          <div
            className={`absolute inset-0 rounded-2xl border-2 ${p.ring} animate-pulse-ring`}
            style={{ animationDelay: '0.7s' }}
          />
          <div
            className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${p.from} ${p.to} flex items-center justify-center shadow-lg animate-gentle-float`}
          >
            <ShieldCheck className="h-8 w-8 text-white" />
          </div>
        </div>

        <BouncingDots size="lg" color={color} />

        {message && (
          <p className={`mt-4 text-sm font-medium ${p.text}`}>{message}</p>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tier 2 — Section loader (dashboard content areas)
// ---------------------------------------------------------------------------
const SectionLoader = ({ message, size = 'md', color = 'blue', className = '' }) => {
  const p = PALETTES[color] || PALETTES.blue;

  return (
    <div className={`flex flex-col items-center justify-center py-12 ${className}`}>
      <BouncingDots size={size} color={color} />
      {message && (
        <p className={`mt-3 text-sm ${p.text}`}>{message}</p>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Tier 3 — Inline loader (buttons, search fields)
// ---------------------------------------------------------------------------
const InlineLoader = ({ color = 'blue', className = '' }) => (
  <span className={`inline-flex items-center ${className}`}>
    <BouncingDots size="sm" color={color} />
  </span>
);

// ---------------------------------------------------------------------------
// Tier 4 — Overlay loader (panels refreshing)
// ---------------------------------------------------------------------------
const OverlayLoader = ({ message, color = 'blue', className = '' }) => (
  <div className={`absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-10 rounded-lg ${className}`}>
    <BouncingDots size="md" color={color} />
    {message && (
      <p className="mt-2 text-xs text-gray-500">{message}</p>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------
const LoadingSpinner = ({
  variant = 'section',
  message,
  size,
  color = 'blue',
  className = '',
}) => {
  switch (variant) {
    case 'page':
      return <PageLoader message={message} color={color} />;
    case 'section':
      return <SectionLoader message={message} size={size} color={color} className={className} />;
    case 'inline':
      return <InlineLoader color={color} className={className} />;
    case 'overlay':
      return <OverlayLoader message={message} color={color} className={className} />;
    default:
      return <SectionLoader message={message} size={size} color={color} className={className} />;
  }
};

export default LoadingSpinner;
export { BouncingDots, PageLoader, SectionLoader, InlineLoader, OverlayLoader };
